import ScoreCard from './ScoreCard'
import Recommendations from './Recommendations'
import { useLocale } from '../locales/useLocale'
import { Globe, Radar, Sparkles, AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react'

const COURSE_CTA_URL = 'https://aeogeo.thinkwithblack.com/learn/seo-101/chapter-1'

const COPY = {
  'zh-TW': {
    overallTitle: '整體可用度',
    overallDesc: '這不是第五個獨立檢查項，而是由下方 3 個面向加權後得到的總分。',
    eligibilityTitle: '基礎訊號完整度',
    eligibilityDesc: '看 title、canonical、作者、日期、Article schema 這些基礎訊號是否齊全。低分不一定代表內容差，可能只是頁面訊號不完整。',
    extractabilityTitle: '答案好不好被抽取',
    extractabilityDesc: '看標題、小節、清單、表格、段落切分與摘要包裝，判斷系統是否容易把這篇拆成可引用的答案。',
    adoptionTitle: '內容值不值得被引用',
    adoptionDesc: '看內容是否有可信度、佐證、第一手經驗與被採用的理由。低分通常代表內容說服力還能再補。',
    sourceInfo: '來源資訊',
    sourceUrl: '分析來源網址',
    finalUrl: '最終轉址網址',
    inputMode: '輸入模式',
    fetchStatus: '抓取狀態',
    observed: '已觀測到的事實',
    strengths: '目前優勢',
    risks: '真正缺失',
    improvements: '可選強化',
    unknowns: '目前無法判定',
    noData: '目前沒有可顯示的項目。',
    noGaps: '本頁無明顯觀測盲區。',
    noPriorityFixes: '目前沒有明顯的基礎缺失。',
    noImprovements: '目前沒有額外的可選強化項目。',
    modeUrl: '網址分析',
    modeManual: '貼上內容',
    recommendations: '建議行動',
    weightedBy: '總分組成',
    weightedFormula: '基礎訊號 30 / 可抽取性 35 / 採用價值 35',
    draggedBy: '目前主要被「{label}」拖低。',
    balanced: '三個面向目前沒有明顯單一短板。',
    extractabilityRuleBase: '規則底分',
    extractabilityLlmDelta: 'LLM 校正',
    extractabilityConfidence: '語義校正信心',
    extractabilityModelOnlyRules: '本次僅使用規則判斷',
    extractabilityModelLabel: '模型',
    extractabilityStrengths: '模型看見的抽取優勢',
    extractabilityIssues: '模型看見的語義問題',
    adoptionStrategyCoverage: '採用理由完整度',
    adoptionEvidenceTrust: '佐證與引用信任',
    adoptionFirsthand: '第一手案例具體度',
    adoptionTransparency: '來源透明度',
    groupFoundation: '基礎訊號',
    groupExtractability: '可抽取性',
    groupTrust: '信任與佐證',
    courseCtaTitle: '下一步：看懂機器怎麼讀你的內容',
    courseCtaBody: '這份分析會告訴你頁面目前卡在基礎訊號、答案可抽取性，還是內容採用價值。如果你想進一步理解，為什麼 title、H2、清單、表格與 Answer-First 佈局，會直接影響內容是否更容易被搜尋系統與 AI 抽取、理解與引用，可以先免費看小黑老師《內容架構學》SEO 101 第一堂。',
    courseCtaButton: '先免費看第一堂',
    courseCtaHint: '從 HTML 結構到 Answer-First 佈局'
  },
  en: {
    overallTitle: 'Overall Readiness',
    overallDesc: 'This is a weighted total from the 3 dimensions below, not a separate fifth check.',
    eligibilityTitle: 'Foundation Signals',
    eligibilityDesc: 'Checks whether title, canonical, author, dates, and Article schema are complete enough to understand the page reliably.',
    extractabilityTitle: 'Answer Extractability',
    extractabilityDesc: 'Checks whether headings, sections, lists, tables, and summaries make the page easy to extract into answer snippets.',
    adoptionTitle: 'Citation Value',
    adoptionDesc: 'Checks whether the content carries trust, evidence, first-hand experience, and a reason to adopt this source.',
    sourceInfo: 'Source Info',
    sourceUrl: 'Source URL',
    finalUrl: 'Final URL',
    inputMode: 'Input Mode',
    fetchStatus: 'Fetch Status',
    observed: 'Observed Facts',
    strengths: 'Strengths',
    risks: 'Missing Foundations',
    improvements: 'Optional Improvements',
    unknowns: 'Cannot Confirm Yet',
    noData: 'No items available.',
    noGaps: 'No clear observation gaps on this page.',
    noPriorityFixes: 'No obvious missing foundation signals.',
    noImprovements: 'No optional improvements suggested right now.',
    modeUrl: 'URL analysis',
    modeManual: 'Manual content',
    recommendations: '推奨アクション',
    weightedBy: 'Score composition',
    weightedFormula: 'Foundation 30 / Extractability 35 / Citation value 35',
    draggedBy: 'The total is mainly dragged down by "{label}".',
    balanced: 'No single dimension is dominating the downside right now.',
    extractabilityRuleBase: 'Rule base',
    extractabilityLlmDelta: 'LLM calibration',
    extractabilityConfidence: 'Calibration confidence',
    extractabilityModelOnlyRules: 'This run used rules only.',
    extractabilityModelLabel: 'Model',
    extractabilityStrengths: 'Model-noted extractability strengths',
    extractabilityIssues: 'Model-noted semantic issues',
    adoptionStrategyCoverage: 'Adoption fit',
    adoptionEvidenceTrust: 'Evidence trust',
    adoptionFirsthand: 'First-hand specificity',
    adoptionTransparency: 'Source transparency',
    groupFoundation: 'Foundation',
    groupExtractability: 'Extractability',
    groupTrust: 'Trust & evidence',
    courseCtaTitle: 'Next step: understand how machines read your content',
    courseCtaBody: 'This report shows whether the page is mainly blocked by foundation signals, extractability, or citation value. If you want to understand why title, H2, lists, tables, and Answer-First layout affect how search systems and AI extract, understand, and cite content, start with Black Teacher’s SEO 101 first lesson.',
    courseCtaButton: 'Watch the free first lesson',
    courseCtaHint: 'From HTML structure to Answer-First layout'
  },
  ja: {
    overallTitle: '全體可用度',
    overallDesc: 'これは別個の 5 つ目の指標ではなく、下の 3 指標を加重した総合点です。',
    eligibilityTitle: '基礎訊號完整度',
    eligibilityDesc: 'title、canonical、著者、日付、Article schema などの基礎訊號が十分かを見ます。',
    extractabilityTitle: '答案是否容易被抽取',
    extractabilityDesc: '見出し、小節、リスト、表、要約の有無から、答案として切り出しやすいかを見ます。',
    adoptionTitle: '內容是否值得被引用',
    adoptionDesc: '信頼性、根拠、一次経験、採用される理由があるかを見ます。',
    sourceInfo: 'ソース情報',
    sourceUrl: '分析元 URL',
    finalUrl: '最終 URL',
    inputMode: '入力モード',
    fetchStatus: '取得状態',
    observed: '観測できた事実',
    strengths: '現在の強み',
    risks: '真正缺失',
    improvements: '任意の強化',
    unknowns: 'まだ判定できない項目',
    noData: '表示できる項目がありません。',
    noGaps: '明確な観測盲区はありません。',
    noPriorityFixes: '明確な基礎欠落はありません。',
    noImprovements: '追加の任意強化はありません。',
    modeUrl: 'URL 分析',
    modeManual: '手動入力',
    recommendations: 'Recommended Actions',
    weightedBy: '総分の内訳',
    weightedFormula: '基礎訊號 30 / 抽取性 35 / 採用価値 35',
    draggedBy: '現在の総分は主に「{label}」に引っ張られています。',
    balanced: '単一の大きな短板は見えていません。',
    extractabilityRuleBase: '規則底分',
    extractabilityLlmDelta: 'LLM 補正',
    extractabilityConfidence: '補正信心',
    extractabilityModelOnlyRules: '今回は規則判定のみです。',
    extractabilityModelLabel: 'モデル',
    extractabilityStrengths: 'モデルが見た抽取優勢',
    extractabilityIssues: 'モデルが見た語義問題',
    adoptionStrategyCoverage: '採用理由の完整度',
    adoptionEvidenceTrust: '根拠と引用の信頼',
    adoptionFirsthand: '一次事例の具体性',
    adoptionTransparency: '情報源の透明性',
    groupFoundation: '基礎訊號',
    groupExtractability: '抽取性',
    groupTrust: '信任與根拠',
    courseCtaTitle: '次の一歩：機械がどう内容を読むかを理解する',
    courseCtaBody: 'この分析は、ページが基礎訊號・抽取性・採用価値のどこで詰まっているかを示します。title、H2、箇条書き、table、Answer-First レイアウトが、検索システムや AI の理解・抽出・引用にどう影響するかを体系的に理解したい場合は、ブラック先生の SEO 101 第一堂から入るのが最短です。',
    courseCtaButton: '無料の第一堂を見る',
    courseCtaHint: 'HTML 構造から Answer-First レイアウトまで'
  }
}

function localeCopy(locale) {
  return COPY[locale] || COPY['zh-TW']
}

function sanitizeList(items) {
  return Array.isArray(items)
    ? items.filter((item) => typeof item === 'string' && item.trim())
    : []
}

function sanitizeFindings(items) {
  return Array.isArray(items)
    ? items.filter((item) => item && typeof item === 'object' && item.title)
    : []
}

function formatSignedNumber(value) {
  const num = Number(value || 0)
  if (!Number.isFinite(num) || num === 0) return '0'
  return num > 0 ? `+${num}` : `${num}`
}

function replaceLabel(template, label) {
  return String(template || '').replace('{label}', label)
}

function resolveFindingGroup(category, copy) {
  if (category === 'eligibility') return copy.groupFoundation
  if (category === 'extractability') return copy.groupExtractability
  if (category === 'adoption') return copy.groupTrust
  return copy.groupFoundation
}

function groupFindings(items, copy) {
  const safeItems = sanitizeFindings(items)
  const groups = new Map()
  safeItems.forEach((item) => {
    const label = resolveFindingGroup(item.category, copy)
    if (!groups.has(label)) groups.set(label, [])
    groups.get(label).push(item)
  })
  return Array.from(groups.entries()).map(([label, entries]) => ({ label, entries }))
}

function CourseCtaPanel({ copy }) {
  return (
    <section className="rounded-2xl border border-primary-200 bg-gradient-to-br from-primary-50 via-white to-blue-50 p-6 shadow-sm">
      <div className="max-w-3xl space-y-3">
        <div className="inline-flex items-center rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-primary-700 border border-primary-100">
          {copy.courseCtaHint}
        </div>
        <h2 className="text-xl font-bold text-gray-900">{copy.courseCtaTitle}</h2>
        <p className="text-sm leading-relaxed text-gray-700">{copy.courseCtaBody}</p>
        <div className="pt-1">
          <a
            href={COURSE_CTA_URL}
            target="_blank"
            rel="sponsored noopener noreferrer"
            className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-primary-700"
          >
            {copy.courseCtaButton}
          </a>
        </div>
      </div>
    </section>
  )
}

function ListPanel({ title, icon: Icon, items, tone = 'gray', emptyLabel }) {
  const classes = {
    gray: 'border-gray-200 bg-white',
    green: 'border-green-200 bg-green-50/50',
    amber: 'border-amber-200 bg-amber-50/50',
    blue: 'border-blue-200 bg-blue-50/50'
  }
  const safeItems = sanitizeList(items)
  return (
    <div className={`rounded-2xl border p-5 ${classes[tone] || classes.gray}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-gray-700" />
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      </div>
      {safeItems.length > 0 ? (
        <ul className="space-y-2 text-sm text-gray-700">
          {safeItems.map((item, index) => (
            <li key={`${title}-${index}`} className="leading-relaxed">{item}</li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500">{emptyLabel}</p>
      )}
    </div>
  )
}

function FindingsPanel({ title, items, emptyLabel, tone = 'amber' }) {
  const safeItems = sanitizeFindings(items)
  const { locale } = useLocale()
  const copy = localeCopy(locale)
  const groupedItems = groupFindings(safeItems, copy)
  const toneClasses = {
    amber: {
      wrap: 'border-amber-200 bg-amber-50/50',
      icon: 'text-amber-700',
      chip: 'bg-amber-100 text-amber-800',
      card: 'border-amber-100'
    },
    blue: {
      wrap: 'border-blue-200 bg-blue-50/50',
      icon: 'text-blue-700',
      chip: 'bg-blue-100 text-blue-800',
      card: 'border-blue-100'
    }
  }
  const classes = toneClasses[tone] || toneClasses.amber
  return (
    <div className={`rounded-2xl border p-5 ${classes.wrap}`}>
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className={`w-4 h-4 ${classes.icon}`} />
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      </div>
      {groupedItems.length > 0 ? (
        <div className="space-y-4">
          {groupedItems.map((group) => (
            <div key={group.label} className="space-y-3">
              <div className="inline-flex rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-gray-700 border border-gray-200">
                {group.label}
              </div>
              <div className="space-y-3">
                {group.entries.map((item, index) => (
                  <div key={`${item.id || item.title}-${index}`} className={`rounded-xl border bg-white p-4 ${classes.card}`}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                      {item.severity && (
                        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${classes.chip}`}>
                          {item.severity}
                        </span>
                      )}
                    </div>
                    {item.evidence && <p className="mt-2 text-sm text-gray-700">{item.evidence}</p>}
                    {item.fixRecommendation && <p className="mt-2 text-xs text-gray-500">{item.fixRecommendation}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">{emptyLabel}</p>
      )}
    </div>
  )
}

export default function ResultsDashboard({
  results,
  recommendations = [],
  feedbackContext,
  apiBaseUrl
}) {
  const { locale, strings } = useLocale()
  const copy = localeCopy(locale)
  const { analysis } = strings

  if (!results) return null

  if (results?.status === 'queued') {
    return (
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6 space-y-4">
        <div className="flex items-center gap-2 text-blue-900">
          <CheckCircle2 className="w-5 h-5" />
          <h2 className="text-lg font-semibold">{analysis.queuedTitle}</h2>
        </div>
        <div className="space-y-2 text-sm text-blue-900">
          <p>{analysis.queuedLine1}</p>
          {results.email && (
            <p>
              {analysis.queuedLine2} <span className="font-medium">{results.email}</span>
            </p>
          )}
          {results.taskId && (
            <p>
              {analysis.taskIdLabel}: <span className="font-mono rounded bg-white px-2 py-1 text-xs">{results.taskId}</span>
            </p>
          )}
          <p className="text-blue-800">{analysis.durationHint}</p>
        </div>
        {results.resultUrl && (
          <div>
            <a
              href={results.resultUrl}
              className="inline-flex items-center rounded-xl border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
            >
              {analysis.checkResults}
            </a>
          </div>
        )}
      </div>
    )
  }

  if (results?.status === 'insufficient_metadata') {
    const unknownSignals = sanitizeList(results?.contentSignals?.unknownSignals)
    return (
      <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-6 space-y-3">
        <div className="flex items-center gap-2 text-yellow-800">
          <AlertTriangle className="w-5 h-5" />
          <h2 className="text-lg font-semibold">{results?.message || 'Metadata 不足'}</h2>
        </div>
        {unknownSignals.length > 0 && (
          <ul className="space-y-1 text-sm text-yellow-900">
            {unknownSignals.map((signal) => <li key={signal}>{signal}</li>)}
          </ul>
        )}
      </div>
    )
  }

  const pageReadinessScore = Number(results?.pageReadinessScore ?? results?.v5Scores?.overallScore ?? 0)
  const eligibilityScore = Number(results?.eligibilityScore ?? 0)
  const extractabilityScore = Number(results?.extractabilityScore ?? results?.v5Scores?.structureScore ?? 0)
  const adoptionScore = Number(results?.adoptionScore ?? results?.v5Scores?.strategyScore ?? 0)

  const strengths = sanitizeList(results?.strengths)
  const observedSignals = sanitizeList(results?.observedSignals)
  const unknowns = sanitizeList(results?.observationGaps || results?.unknowns)
  const findings = sanitizeFindings(results?.priorityFixes || results?.findings)
  const recommendedImprovements = sanitizeFindings(results?.recommendedImprovements)
  const recommendedActions = Array.isArray(results?.recommendedActions) && results.recommendedActions.length
    ? results.recommendedActions
    : recommendations
  const extractabilityBreakdown = results?.extractabilityBreakdown || null
  const calibrationEvidence = extractabilityBreakdown?.evidence || {}
  const adoptionBreakdown = results?.adoptionBreakdown || null
  const extractabilityFooter = (
    <div className="space-y-3">
      <div className="grid gap-2 text-xs text-gray-700">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="flex items-center justify-between gap-3">
            <span className="font-semibold text-gray-800">{copy.extractabilityRuleBase}</span>
            <span>{extractabilityBreakdown?.ruleBaseScore ?? extractabilityScore}</span>
          </div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="font-semibold text-gray-800">{copy.extractabilityLlmDelta}</span>
            <span>{formatSignedNumber(extractabilityBreakdown?.llmCalibrationDelta)}</span>
          </div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="font-semibold text-gray-800">{copy.extractabilityConfidence}</span>
            <span>{extractabilityBreakdown?.calibrationConfidence || 'unavailable'}</span>
          </div>
          {extractabilityBreakdown?.model && (
            <div className="mt-2 flex items-center justify-between gap-3">
              <span className="font-semibold text-gray-800">{copy.extractabilityModelLabel}</span>
              <span>{extractabilityBreakdown.model}</span>
            </div>
          )}
        </div>
      </div>
      {!extractabilityBreakdown?.model && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs text-gray-600">{copy.extractabilityModelOnlyRules}</p>
        </div>
      )}
      {Array.isArray(calibrationEvidence?.strengths) && calibrationEvidence.strengths.length > 0 && (
        <div className="rounded-lg border border-green-100 bg-green-50 p-3">
          <p className="text-xs font-semibold text-green-800">{copy.extractabilityStrengths}</p>
          <ul className="mt-2 space-y-1 text-xs text-green-800">
            {calibrationEvidence.strengths.map((item) => (
              <li key={item.id || item.title}>{item.title || item.evidence}</li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(calibrationEvidence?.issues) && calibrationEvidence.issues.length > 0 && (
        <div className="rounded-lg border border-amber-100 bg-amber-50 p-3">
          <p className="text-xs font-semibold text-amber-800">{copy.extractabilityIssues}</p>
          <ul className="mt-2 space-y-1 text-xs text-amber-800">
            {calibrationEvidence.issues.map((item) => (
              <li key={item.id || item.title}>{item.title || item.evidence}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
  const adoptionFooter = adoptionBreakdown ? (
    <div className="space-y-3">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
        <div className="flex items-center justify-between gap-3">
          <span className="font-semibold text-gray-800">{copy.adoptionStrategyCoverage}</span>
          <span>{adoptionBreakdown.strategyCoverageScore ?? 0}</span>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="font-semibold text-gray-800">{copy.adoptionEvidenceTrust}</span>
          <span>{adoptionBreakdown.evidenceTrustScore ?? 0}</span>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="font-semibold text-gray-800">{copy.adoptionFirsthand}</span>
          <span>{adoptionBreakdown.firsthandSpecificityScore ?? 0}</span>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="font-semibold text-gray-800">{copy.adoptionTransparency}</span>
          <span>{adoptionBreakdown.sourceTransparencyScore ?? 0}</span>
        </div>
      </div>
    </div>
  ) : null

  const inputMode = results?.inputMode === 'url' ? copy.modeUrl : copy.modeManual
  const lowestDimension = [
    { key: 'eligibility', label: copy.eligibilityTitle, score: eligibilityScore },
    { key: 'extractability', label: copy.extractabilityTitle, score: extractabilityScore },
    { key: 'adoption', label: copy.adoptionTitle, score: adoptionScore }
  ].sort((a, b) => a.score - b.score)[0]
  const overallFooter = (
    <div className="space-y-3">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
        <p className="text-xs font-semibold text-gray-700">{copy.weightedBy}</p>
        <p className="mt-1 text-xs text-gray-600">{copy.weightedFormula}</p>
      </div>
      <div className="rounded-lg border border-orange-100 bg-orange-50 p-3">
        <p className="text-xs text-orange-800">
          {lowestDimension?.score < 60 ? replaceLabel(copy.draggedBy, lowestDimension.label) : copy.balanced}
        </p>
      </div>
    </div>
  )

  return (
    <div className="space-y-8">
      <section className="grid gap-6 lg:grid-cols-4">
        <ScoreCard title={copy.overallTitle} score={pageReadinessScore} description={copy.overallDesc} footer={overallFooter} />
        <ScoreCard title={copy.eligibilityTitle} score={eligibilityScore} description={copy.eligibilityDesc} />
        <ScoreCard title={copy.extractabilityTitle} score={extractabilityScore} description={copy.extractabilityDesc} footer={extractabilityFooter} />
        <ScoreCard title={copy.adoptionTitle} score={adoptionScore} description={copy.adoptionDesc} footer={adoptionFooter} />
      </section>

      <section className="rounded-2xl border border-blue-200 bg-blue-50/40 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-700" />
          <h2 className="text-lg font-semibold text-gray-900">{copy.sourceInfo}</h2>
        </div>
        <div className="grid gap-2 text-sm text-gray-700 md:grid-cols-2">
          <div><span className="font-semibold text-gray-900">{copy.inputMode}：</span>{inputMode}</div>
          {results?.sourceUrl && <div><span className="font-semibold text-gray-900">{copy.sourceUrl}：</span>{results.sourceUrl}</div>}
          {results?.finalUrl && <div><span className="font-semibold text-gray-900">{copy.finalUrl}：</span>{results.finalUrl}</div>}
          {results?.fetchMeta?.status && <div><span className="font-semibold text-gray-900">{copy.fetchStatus}：</span>{results.fetchMeta.status}</div>}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <ListPanel title={copy.observed} icon={Radar} items={observedSignals} tone="blue" emptyLabel={copy.noData} />
        <ListPanel title={copy.strengths} icon={CheckCircle2} items={strengths} tone="green" emptyLabel={copy.noData} />
        <FindingsPanel title={copy.risks} items={findings} emptyLabel={copy.noPriorityFixes} />
        <FindingsPanel title={copy.improvements} items={recommendedImprovements} emptyLabel={copy.noImprovements} tone="blue" />
        <ListPanel title={copy.unknowns} icon={HelpCircle} items={unknowns} tone="gray" emptyLabel={copy.noGaps} />
      </section>

      {recommendedActions.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center gap-2 text-gray-700">
            <Sparkles className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold">{copy.recommendations}</h2>
          </div>
          <Recommendations
            recommendations={recommendedActions}
            feedbackContext={feedbackContext}
            apiBaseUrl={apiBaseUrl}
            selectedChunkIds={[]}
          />
        </section>
      )}

      <CourseCtaPanel copy={copy} />
    </div>
  )
}
